/**
 * useCollaboration — conditionally initializes Yjs collaboration for a chapter.
 *
 * Rules:
 *  - Solo mode (owner, no active members): returns null → editor stays plain Tiptap
 *  - Collaborative mode (shared project or non-owner member): returns extensions + presence
 */
import { useState, useEffect } from 'react';
import type { WebsocketProvider } from 'y-websocket';
import {
  initializeCollaboration,
  destroyCollaboration,
  getCollaborationExtensions,
  type CollaboratorUser,
} from '../lib/collaboration';
import type { Extension } from '@tiptap/core';

export interface CollaborationInfo {
  /** Tiptap extensions to inject (Collaboration + CursorPlugin) */
  extensions: Extension[];
  /** Currently connected remote users (for presence UI) */
  remoteUsers: CollaboratorUser[];
  /** Whether the WebSocket is connected */
  connected: boolean;
  /** y-websocket provider — used to seed the doc safely once synced. */
  provider: WebsocketProvider;
}

interface UseCollaborationOptions {
  projectId: string;
  chapterId: string;
  userId: string;
  userName: string;
  /** Whether the current user is the project owner */
  isOwner: boolean;
}

/**
 * Returns collaboration info if the project is shared, otherwise null.
 * Cleans up the collaboration provider when chapterId changes or component unmounts.
 */
export function useCollaboration({
  projectId,
  chapterId,
  userId,
  userName,
}: UseCollaborationOptions): CollaborationInfo | null {
  const [isShared, setIsShared] = useState<boolean | null>(null);
  const [remoteUsers, setRemoteUsers] = useState<CollaboratorUser[]>([]);
  const [connected, setConnected] = useState(false);
  // Store extensions in state so they're stable across renders
  const [extensions, setExtensions] = useState<Extension[]>([]);
  const [provider, setProvider] = useState<WebsocketProvider | null>(null);

  // Real-time sync ligado — inclusive o MESMO usuário em 2 aparelhos. O conteúdo
  // é semeado com segurança (chapter-editor: só semeia após sincronizar, e o
  // autosave é travado até sincronizar → nunca apaga o livro). O WS é blindado
  // contra erro de handler, e documentStates tem constraint unique pro persist.
  useEffect(() => {
    setIsShared(true);
  }, [projectId]);

  // Initialize collaboration once the project is shared. init and destroy are
  // PAIRED in this one effect (init on run, destroy in cleanup) so the instance's
  // refcount stays balanced no matter how often this effect re-runs or how many
  // EditorViews mount the same chapter during a transition. destroyCollaboration
  // only tears the socket down when the LAST consumer leaves (see collaboration.ts).
  useEffect(() => {
    if (isShared !== true) return;
    if (!chapterId || !userId) return;

    const collab = initializeCollaboration(chapterId, userId, userName, (users) => {
      setRemoteUsers(users);
    });
    setExtensions(getCollaborationExtensions(chapterId));
    setProvider(collab.provider);

    const onStatus = (event: { status: string }) => {
      setConnected(event.status === 'connected');
    };
    collab.provider.on('status', onStatus);

    return () => {
      collab.provider.off('status', onStatus);
      destroyCollaboration(chapterId);
    };
  }, [isShared, chapterId, userId, userName]);

  // Not yet determined or not shared → solo mode
  if (isShared !== true || extensions.length === 0 || !provider) return null;

  return {
    extensions,
    remoteUsers,
    connected,
    provider,
  };
}
